import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Grid, 
    Card, 
    CardContent, 
    Button, 
    TextField, 
    FormControl, 
    RadioGroup, 
    FormControlLabel, 
    Radio,
    Alert,
    Switch,
    FormGroup,
    Checkbox
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useUserContext } from '../../../context/user_context/UserContext';
import { useOrganizationContext } from '../../../context/organization_context/OrganizationContext';
import { useToast, ToastType } from '../../../context/toast/ToastContext';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../resources/strings/StringKeys';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';
import { 
    getEmployeePerformanceReview, 
    getOrganizationPerformanceReview,
    updatePerformanceReviewResponses,
    updateReviewAcknowledgment
} from '../../../repository/PerformanceReviewDAO';
import { Tooltip } from '@mui/material';
import { useLoadingContext } from '../../../context/loading/LoadingContext';
import { Loading } from '../../../components/loading/Loading';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { PDFDownloadLink } from '@react-pdf/renderer';
import PerformanceReviewPDF from './PerformanceReviewPDF';
import { Print } from '@mui/icons-material';
import { translateReviewCycle } from '../../../utils/translations/ReviewCycleUtils';

const PERFORMANCE_RATINGS = [
    { value: 'greatly_exceeds', labelKey: StringKeys.GREATLY_EXCEEDS_EXPECTATIONS },
    { value: 'exceeds', labelKey: StringKeys.EXCEEDS_EXPECTATIONS },
    { value: 'meets', labelKey: StringKeys.MEETS_EXPECTATIONS },
    { value: 'below', labelKey: StringKeys.BELOW_EXPECTATIONS },
    { value: 'far_below', labelKey: StringKeys.FAR_BELOW_EXPECTATIONS }
];

const PerformanceReviewDetail = () => {
    const { reviewId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { firebaseUser } = useUserContext();
    const { organizationEmployees, getEmployeeByID } = useOrganizationContext();
    const { showToast } = useToast();

    const [review, setReview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isManager, setIsManager] = useState(false);
    const [responses, setResponses] = useState({});
    const [error, setError] = useState(null);
    const [employeeId, setEmployeeId] = useState()
    const [isEditable, setIsEditable] = useState(false);
    const [previousResponses, setPreviousResponses] = useState({});
    const [viewMode, setViewMode] = useState('employee');
    const [isManagerReviewAvailable, setIsManagerReviewAvailable] = useState(false);
    const { setLoadingData } = useLoadingContext();
    const [openUnlockDialog, setOpenUnlockDialog] = useState(false);
    const [acknowledgementStatus, setAcknowledgementStatus] = useState({
        employee: false,
        manager: false
    });
    const [managerResponses, setManagerResponses] = useState({});
    const [employeeResponses, setEmployeeResponses] = useState({});
    const [showPrintButton, setShowPrintButton] = useState(false);
    const [organizationLogo, setOrganizationLogo] = useState(null);

    useEffect(() => {
        const getEmployeeId = async () => {
            const result = await getOrganizationPerformanceReview(firebaseUser.organization, reviewId)
            setEmployeeId(result.employee_id)
        }
        if(firebaseUser && reviewId) {
            getEmployeeId()
        }
    },[reviewId])

    useEffect(() => {
        // Create an async function inside the effect
        const fetchReviewDetails = async () => {
            try {
                setLoading(true);
                const organizationId = firebaseUser.organization;

                const result = await getEmployeePerformanceReview(
                    organizationId, 
                    employeeId, 
                    reviewId
                );

                if (result.success) {
                    if (!result.data || !Array.isArray(result.data.questions)) {
                        setError('Invalid review data structure');
                        return;
                    }

                    setReview(result.data);
                    
                    const isCurrentUserManager = employeeId !== firebaseUser.uid;
                    setIsManager(isCurrentUserManager);

                    const employeeSubmitted = result.data.employee_submitted_at !== null;
                    const managerSubmitted = result.data.manager_submitted_at !== null;

                    if (isCurrentUserManager) {
                        setViewMode('manager');
                        const managerResponses = {};
                        result.data.questions.forEach(question => {
                            const responses = question.responses.manager;
                            if (responses) {
                                managerResponses[question.id] = {
                                    value: responses.answer || '',
                                    type: question.type
                                };
                                if (question.type === 'multipleChoice') {
                                    managerResponses[`${question.id}_comments`] = {
                                        value: responses.comments || ''
                                    };
                                }
                            }
                        });
                        setResponses(managerResponses);
                        setIsManagerReviewAvailable(true); // Always true for manager
                    } else {
                        setViewMode('employee');
                        setIsManagerReviewAvailable(result.data.manager_submitted_at !== null);
                    }

                    // Modify the isEditable logic for employees
                    const isCurrentUserAllowedToEdit =
                        (isCurrentUserManager && result.data.editable_by === 'manager') ||
                        (!isCurrentUserManager && (
                            result.data.editable_by === 'employee' && 
                            (
                                !result.data.employee_submitted_at || // Not submitted yet
                                result.data.status?.employee === 'PENDING' // Or status is pending
                            )
                        ));
                    
                    const isEmployeeReviewComplete = result.data.employee_submitted_at !== null;
                    const managerEditableCondition = 
                        isCurrentUserManager 
                            ? (isCurrentUserAllowedToEdit && result.data.employee_submitted_at !== null) 
                            : isCurrentUserAllowedToEdit;

                    setIsEditable(managerEditableCondition);

                    const userRole = isCurrentUserManager ? 'manager' : 'employee';
                    const initialResponses = {};
                    
                    result.data.questions.forEach(question => {
                        const response = question.responses[userRole] || {};
                        initialResponses[question.id] = {
                            value: response.answer || '',
                            type: question.type
                        };
                        
                        if (question.type === 'multipleChoice') {
                            initialResponses[`${question.id}_comments`] = {
                                value: response.comments || '',
                                type: 'comments'
                            };
                        }
                    });

                    setResponses(initialResponses);
                    setPreviousResponses(initialResponses);

                    setAcknowledgementStatus({
                        employee: result.data.acknowledgments?.employee_acknowledged || false,
                        manager: result.data.acknowledgments?.manager_acknowledged || false
                    });

                    const initialManagerResponses = {};
                    const initialEmployeeResponses = {};
                    
                    result.data.questions.forEach(question => {
                        const managerResponse = question.responses.manager || {};
                        initialManagerResponses[question.id] = {
                            value: managerResponse.answer || '',
                            type: question.type
                        };
                        if (question.type === 'multipleChoice') {
                            initialManagerResponses[`${question.id}_comments`] = {
                                value: managerResponse.comments || '',
                                type: 'comments'
                            };
                        }

                        const employeeResponse = question.responses.employee || {};
                        initialEmployeeResponses[question.id] = {
                            value: employeeResponse.answer || '',
                            type: question.type
                        };
                        if (question.type === 'multipleChoice') {
                            initialEmployeeResponses[`${question.id}_comments`] = {
                                value: employeeResponse.comments || '',
                                type: 'comments'
                            };
                        }
                    });

                    setManagerResponses(initialManagerResponses);
                    setEmployeeResponses(initialEmployeeResponses);
                    setPreviousResponses(isManager ? initialManagerResponses : initialEmployeeResponses);
                } else {
                    setError(result.error || 'Failed to fetch performance review');
                    showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
                }
            } catch (error) {
                console.error('Error fetching review details:', error);
                setError(error.message || 'An unexpected error occurred');
                showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
            } finally {
                setLoading(false);
            }
        };

        if (firebaseUser && employeeId && reviewId) {
            fetchReviewDetails();
        }
    }, [reviewId, firebaseUser, employeeId, t, showToast]);

    useEffect(() => {
        if (review?.acknowledgments?.employee_acknowledged && 
            review?.acknowledgments?.manager_acknowledged) {
            setShowPrintButton(true);
        }
    }, [review]);

    useEffect(() => {
        // Fetch organization logo if available
        if (firebaseUser?.organization_logo) {
            setOrganizationLogo(firebaseUser.organization_logo);
        }
    }, [firebaseUser]);

    const handleResponseChange = (questionId, value, type = 'text') => {
        const updateFn = viewMode === 'manager' ? setManagerResponses : setEmployeeResponses;
        
        updateFn(prev => {
            const newResponses = { ...prev };
            if (type === 'multipleChoice') {
                newResponses[questionId] = {
                    value: value,
                    type: type
                };
            } else if (type === 'comments') {
                newResponses[questionId] = {
                    value: value,
                    type: type
                };
            } else {
                newResponses[questionId] = {
                    value: value,
                    type: 'freeForm'
                };
            }
            return newResponses;
        });
    };

    const handleSubmit = async () => {
        setLoadingData(true);
        
        const currentResponses = viewMode === 'manager' ? managerResponses : employeeResponses;
        const previousState = viewMode === 'manager' ? previousResponses : employeeResponses;

        const hasChanges = Object.keys(currentResponses).some(
            key => currentResponses[key]?.value !== previousState[key]?.value
        );

        if (!hasChanges && !isEditable) {
            showToast(t(StringKeys.NO_CHANGES_TO_SUBMIT), ToastType.INFO);
            setLoadingData(false);
            return;
        }

        try {
            const validationErrors = review.questions.reduce((errors, question) => {
                if (question.type === 'multipleChoice') {
                    if (!currentResponses[question.id]?.value) {
                        errors.push(`Please select a rating for: ${question.text}`);
                    }
                }
                return errors;
            }, []);

            if (validationErrors.length > 0) {
                showToast(validationErrors[0], ToastType.DANGER);
                setLoadingData(false);
                return;
            }

            const preparedResponses = review.questions.reduce((acc, question) => {
                const response = currentResponses[question.id];
                const comments = currentResponses[`${question.id}_comments`];

                acc[question.id] = {
                    type: question.type,
                    rating: response?.value || '',
                    comments: comments?.value || '',
                    text: question.text
                };

                return acc;
            }, {});

            const result = await updatePerformanceReviewResponses({
                organizationId: firebaseUser.organization,
                employeeId: review.employee_id,
                reviewId: reviewId,
                responses: preparedResponses,
                isManager: isManager
            });

            if (result.success) {
                showToast(t(StringKeys.REVIEW_SUBMITTED_SUCCESSFULLY), ToastType.SUCCESS);
                setLoadingData(false);
                navigate('/performance-reviews');
            } else {
                setLoadingData(false);
                showToast(result.error || t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
            }
        } catch (error) {
            console.error('Submit error:', error);
            setLoadingData(false);
            showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
        }
    };

    const handleViewModeChange = () => {
        if (isManager || isManagerReviewAvailable) {
            setViewMode(prev => prev === 'employee' ? 'manager' : 'employee');
        }
    };

    const handleUnlockRequest = () => {
        setOpenUnlockDialog(false);
        //TODO: Implement
        showToast(t(StringKeys.PERMISSION_REQUEST_SENT_TO_MANAGER), ToastType.SUCCESS);
    };

    const handleAcknowledgement = async () => {
        try {
            setLoadingData(true);
            
            if (!review.employee_submitted_at || !review.manager_submitted_at) {
                showToast(t(StringKeys.BOTH_REVIEWS_MUST_BE_COMPLETED_BEFORE_ACKNOWLEDGEMENT), ToastType.DANGER);
                setLoadingData(false);
                return;
            }

            const result = await updateReviewAcknowledgment({
                organizationId: firebaseUser.organization,
                employeeId: review.employee_id,
                reviewId: reviewId,
                isManager: isManager
            });

            if (result.success) {
                // Update local state
                setAcknowledgementStatus(prev => ({
                    ...prev,
                    [isManager ? 'manager' : 'employee']: true
                }));
                
                // Update review state with new acknowledgments
                setReview(prev => ({
                    ...prev,
                    acknowledgments: result.acknowledgments
                }));

                showToast(t(StringKeys.SUCCESS), ToastType.SUCCESS);
                
                // Show print button if both have acknowledged
                if (result.acknowledgments.employee_acknowledged && 
                    result.acknowledgments.manager_acknowledged) {
                    setShowPrintButton(true);
                }
            } else {
                showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
            }
            
            setLoadingData(false);
        } catch (error) {
            console.error('Acknowledgement error:', error);
            setLoadingData(false);
            showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
        }
    };

    const renderQuestionInput = (question) => {
        if (!question) return null;

        const currentResponses = viewMode === 'manager' ? managerResponses : employeeResponses;
        const isCurrentUserEditing = 
            (viewMode === 'employee' && !isManager) ||
            (viewMode === 'manager' && isManager);
        const isQuestionLocked = (!isEditable && isCurrentUserEditing) || !isCurrentUserEditing;

        const inputProps = {
            sx: {
                ...(((!isEditable && isCurrentUserEditing) || !isCurrentUserEditing) && {
                    backgroundColor: 'transparent',
                    cursor: 'not-allowed',
                    '& .MuiInputBase-input': {
                        color: 'text.primary',
                        WebkitTextFillColor: 'text.primary',
                        '-webkit-text-fill-color': 'text.primary',
                        opacity: '0.8 !important'
                    },
                    '& .MuiFormControlLabel-label': {
                        color: 'text.primary',
                        opacity: 0.8
                    },
                    '& .MuiRadio-root.Mui-disabled': {
                        color: 'text.secondary'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'divider'
                    }
                })
            }
        };

        const QuestionHeader = () => (
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 2,
                gap: 1
            }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {question.text}
                </Typography>
                {/* {isQuestionLocked && !isManager && (
                    <Tooltip title="Click to request edit permission">
                        <LockIcon 
                            sx={{ 
                                fontSize: '1rem',
                                color: 'action.disabled',
                                cursor: 'pointer',
                                '&:hover': {
                                    color: 'primary.main'
                                }
                            }}
                            onClick={() => setOpenUnlockDialog(true)}
                        />
                    </Tooltip>
                )} */}
            </Box>
        );

        switch (question.type) {
            case 'multipleChoice':
                return (
                    <Box>
                        <QuestionHeader />
                        <Card 
                            variant="outlined" 
                            sx={{
                                opacity: (!isEditable || !isCurrentUserEditing) ? 0.8 : 1,
                                transition: 'opacity 0.2s ease',
                                '&:hover': {
                                    opacity: (!isEditable || !isCurrentUserEditing) ? 0.85 : 1
                                },
                                backgroundColor: 'background.paper',
                                borderColor: 'divider'
                            }}
                        >
                            <CardContent>
                                <FormControl component="fieldset" fullWidth>
                                    <RadioGroup
                                        value={currentResponses[question.id]?.value || ''}
                                        onChange={(e) => handleResponseChange(
                                            question.id, 
                                            e.target.value, 
                                            'multipleChoice'
                                        )}
                                        {...inputProps}
                                    >
                                        {PERFORMANCE_RATINGS.map((rating) => (
                                            <FormControlLabel 
                                                key={rating.value} 
                                                value={rating.value} 
                                                control={
                                                    <Radio 
                                                        disabled={!isEditable || viewMode !== (isManager ? 'manager' : 'employee')} 
                                                    />
                                                } 
                                                label={t(rating.labelKey)}
                                                sx={{
                                                    '& .MuiFormControlLabel-label': {
                                                        color: 'text.primary',
                                                        opacity: (!isEditable || !isCurrentUserEditing) ? 0.8 : 1
                                                    }
                                                }}
                                            />
                                        ))}
                                    </RadioGroup>
                                </FormControl>
                                
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label={t(StringKeys.ADDITIONAL_COMMENTS)}
                                    value={currentResponses[`${question.id}_comments`]?.value || ''}
                                    onChange={(e) => handleResponseChange(
                                        `${question.id}_comments`, 
                                        e.target.value, 
                                        'comments'
                                    )}
                                    variant="outlined"
                                    margin="normal"
                                    placeholder="Provide context or explanation for your rating (optional)"
                                    disabled={!isEditable || viewMode !== (isManager ? 'manager' : 'employee')}
                                    {...inputProps}
                                    sx={{
                                        ...inputProps.sx,
                                        '& .MuiInputBase-root': {
                                            backgroundColor: 'transparent',
                                        },
                                        '& .MuiInputBase-input': {
                                            color: 'text.primary !important',
                                            WebkitTextFillColor: 'text.primary !important',
                                            '-webkit-text-fill-color': 'text.primary !important'
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: 'text.secondary',
                                            '&.Mui-disabled': {
                                                color: 'text.secondary',
                                                opacity: 0.8
                                            }
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            '&.Mui-disabled': {
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'divider'
                                                }
                                            }
                                        }
                                    }}
                                />
                            </CardContent>
                        </Card>
                    </Box>
                );
            case 'freeForm':
            default:
                return (
                    <Box>
                        <QuestionHeader />
                        <Card 
                            variant="outlined" 
                            sx={{
                                opacity: (!isEditable || !isCurrentUserEditing) ? 0.9 : 1,
                                transition: 'opacity 0.2s ease',
                                '&:hover': {
                                    opacity: (!isEditable || !isCurrentUserEditing) ? 0.95 : 1
                                }
                            }}
                        >
                            <CardContent>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    placeholder={t(StringKeys.YOUR_RESPONSE_HERE)}
                                    value={currentResponses[question.id]?.value || ''}
                                    onChange={(e) => handleResponseChange(question.id, e.target.value)}
                                    variant="outlined"
                                    margin="normal"
                                    disabled={!isEditable || viewMode !== (isManager ? 'manager' : 'employee')}
                                    {...inputProps}
                                />
                            </CardContent>
                        </Card>
                    </Box>
                );
        }
    };

    const UnlockDialog = () => (
        <Dialog open={openUnlockDialog} onClose={() => setOpenUnlockDialog(false)}>
            <DialogTitle>{t(StringKeys.REQUEST_EDIT_PERMISSION)}</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {t(StringKeys.THIS_REVIEW_HAS_BEEN_SUBMITTED_AND_IS_LOCKED_)}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpenUnlockDialog(false)}>{t(StringKeys.CANCEL)}</Button>
                <Button onClick={handleUnlockRequest} variant="contained" color="primary">
                    {t(StringKeys.SEND_REQUEST)}
                </Button>
            </DialogActions>
        </Dialog>
    );

    const AcknowledgementSection = () => {
        const bothReviewsSubmitted = review.employee_submitted_at && review.manager_submitted_at;
        if (!bothReviewsSubmitted) return null;

        // Determine the role based on viewMode rather than isManager
        const acknowledgeWithRole = viewMode === 'manager' ? 
            t(StringKeys.EMPLOYEE) : 
            t(StringKeys.MANAGER);

        return (
            <Card sx={{ mt: 4, mb: 4 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {t(StringKeys.REVIEW_ACKNOWLEDGEMENT)}
                    </Typography>
                    
                    <Box sx={{ mt: 2 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={review.acknowledgments?.[viewMode === 'manager' ? 'manager_acknowledged' : 'employee_acknowledged'] || false}
                                    onChange={() => handleAcknowledgement()}
                                    disabled={review.acknowledgments?.[viewMode === 'manager' ? 'manager_acknowledged' : 'employee_acknowledged']}
                                />
                            }
                            label={
                                <Typography>
                                    {t(StringKeys.I_ACKNOWLEDGE_THAT_I_HAVE_REVIEWED_AND_DISCUSSED_THIS_PERFORMANCE_REVIEW_WITH_MY_X, 
                                        {x: acknowledgeWithRole})}.
                                </Typography>
                            }
                        />
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                            {t(StringKeys.EMPLOYEE_ACKNOWLEDGEMENT)}: {review.acknowledgments?.employee_acknowledged ? 
                                t(StringKeys.ACKNOWLEDGED) : t(StringKeys.PENDING)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {t(StringKeys.MANAGER_ACKNOWLEDGEMENT)}: {review.acknowledgments?.manager_acknowledged ? 
                                t(StringKeys.ACKNOWLEDGED) : t(StringKeys.PENDING)}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    };

    if (loading) return <AppSidebar content={<Loading />} />

    if (error) {
        return (
            <AppSidebar 
                content={
                    <Box sx={{ p: 3 }}>
                        <Alert severity="error">{error}</Alert>
                        <Button 
                            variant="contained" 
                            onClick={() => navigate('/performance-reviews')}
                            sx={{ mt: 2 }}
                        >
                            {t(StringKeys.BACK_TO_REVIEWS)}
                        </Button>
                    </Box>
                } 
            />
        );
    }

    if (!review || !Array.isArray(review.questions)) {
        return (
            <AppSidebar 
                content={
                    <Box sx={{ p: 3 }}>
                        <Alert severity="warning">
                            {t(StringKeys.NO_QUESTIONS_AVAILABLE)}
                        </Alert>
                    </Box>
                } 
            />
        );
    }

    return (
        <AppSidebar 
            content={
                <Box sx={{ p: 3 }}>
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' }, 
                        justifyContent: 'space-between', 
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        mb: 3 
                    }}>
                        <Box>
                            <Typography variant="h5" gutterBottom>
                                {review.title}
                            </Typography>
                            <Typography variant="subtitle1" color="textSecondary">
                                {t(StringKeys.REVIEW_CYCLE)}: {translateReviewCycle(review.review_cycle, t)}
                            </Typography>
                            <Typography variant="subtitle1" color="textSecondary">
                                {t(StringKeys.DUE_DATE)}: {new Date(review.due_date.seconds * 1000).toLocaleDateString()}
                            </Typography>
                        </Box>
                        
                        {(isManagerReviewAvailable || !isManager) && (
                            <Box sx={{ 
                                ml: { xs: 0, sm: 2 },
                                mt: { xs: 2, sm: 0 },
                                p: 2, 
                                bgcolor: 'background.paper',
                                borderRadius: 1,
                                boxShadow: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                width: { xs: '100%', sm: 'auto' }
                            }}>
                                {showPrintButton && (
                                    <PDFDownloadLink
                                        document={
                                            <PerformanceReviewPDF 
                                                review={review}
                                                employee={getEmployeeByID(employeeId)}
                                                manager={getEmployeeByID(review.manager_id)}
                                                organizationLogo={organizationLogo}
                                            />
                                        }
                                        fileName={`performance-review-${review.review_cycle}-${getEmployeeByID(employeeId).last_name}.pdf`}
                                        style={{ textDecoration: 'none' }}
                                    >
                                        {({ blob, url, loading, error }) => (
                                            <Button
                                                variant="contained"
                                                startIcon={<Print />}
                                                disabled={loading}
                                                fullWidth
                                                sx={{
                                                    backgroundColor: 'primary.main',
                                                    '&:hover': {
                                                        backgroundColor: 'primary.dark',
                                                    },
                                                }}
                                            >
                                                {loading ? t(StringKeys.DOWNLOADING_PDF) : t(StringKeys.DOWNLOAD_PDF)}
                                            </Button>
                                        )}
                                    </PDFDownloadLink>
                                )}

                                <Tooltip 
                                    title={
                                        !isManagerReviewAvailable 
                                            ? (isManager 
                                                ? t(StringKeys.SWITCH_WILL_BE_AVAILABLE_AFTER_EMPLOYEE_SUBMITS_THEIR_REVIEW)
                                                : t(StringKeys.SWITCH_WILL_BE_AVAILABLE_AFTER_MANAGER_SUBMITS_THEIR_REVIEW)
                                            )
                                            : ""
                                    }
                                >
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={viewMode === 'manager'}
                                                    onChange={handleViewModeChange}
                                                    disabled={!isManagerReviewAvailable}
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {viewMode === 'employee' 
                                                        ? t(StringKeys.SWITCH_TO_MANAGER_EVALUATION)
                                                        : t(StringKeys.SWITCH_TO_SELF_EVALUATION)
                                                    }
                                                </Typography>
                                            }
                                        />
                                    </FormGroup>
                                </Tooltip>
                            </Box>
                        )}
                    </Box>

                    {/* Submission Status Section */}
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between', 
                        mb: 3, 
                        p: 2, 
                        backgroundColor: 'background.paper', 
                        borderRadius: 1,
                        boxShadow: 1,
                        gap: { xs: 2, sm: 0 }
                    }}>
                        <Box>
                            <Typography variant="subtitle2">
                                {t(StringKeys.EMPLOYEE_SUBMISSION)}
                            </Typography>
                            <Typography variant="body2">
                                {review.employee_submitted_at 
                                    ? `${t(StringKeys.SUBMITTED_ON)}: ${new Date(review.employee_submitted_at.seconds * 1000).toLocaleString(undefined, {
                                        year: 'numeric',
                                        month: 'numeric',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}`
                                    : t(StringKeys.NOT_YET_SUBMITTED)
                                }
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="subtitle2">
                                {t(StringKeys.MANAGER_SUBMISSION)}
                            </Typography>
                            <Typography variant="body2">
                                {review.manager_submitted_at 
                                    ? `${t(StringKeys.SUBMITTED_ON)}: ${new Date(review.manager_submitted_at.seconds * 1000).toLocaleString(undefined, {
                                        year: 'numeric',
                                        month: 'numeric',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}`
                                    : t(StringKeys.NOT_YET_SUBMITTED)
                                }
                            </Typography>
                        </Box>
                    </Box>

                    {isManager && !isEditable && !review.employee_submitted_at && (
                        <Alert 
                            severity="error" 
                            sx={{ 
                                mb: 2, 
                                '& .MuiAlert-message': { 
                                    width: '100%', 
                                    textAlign: 'center' 
                                } 
                            }}
                        >
                            {t(StringKeys.YOU_CANNOT_PERFORM_YOUR_REVIEW_UNTIL_THE_EMPLOYEE_COMPLETES_THEIR_SELF_EVALUATION)}
                        </Alert>
                    )}

                    {/* Rest of the existing content */}
                    {['performance', 'goals', 'feedback'].map((section) => {
                        // Filter questions for this section
                        const sectionQuestions = (review.questions || []).filter(q => q.section === section);
                        
                        // Only render the section if it has questions
                        return sectionQuestions.length > 0 ? (
                            <Box key={section} sx={{ mb: 4 }}>
                                <Typography variant="h6" sx={{ mb: 2, textTransform: 'capitalize' }}>
                                    {section} Section
                                </Typography>
                                <Grid container spacing={3}>
                                    {sectionQuestions.map((question, index) => (
                                        <Grid item xs={12} key={question.id || index}>
                                            {renderQuestionInput(question)}
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        ) : null;
                    })}

                    <AcknowledgementSection />

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                        {isEditable && (
                            <Button 
                                variant="contained" 
                                color="primary" 
                                onClick={handleSubmit}
                            >
                                {t(StringKeys.SUBMIT)}
                            </Button>
                        )}
                    </Box>

                    {/* <UnlockDialog /> */}
                </Box>
            }
        />
    );
};

export default PerformanceReviewDetail;
